import { isEmpty } from 'lodash';
import { default as Router } from 'next/router';
import React from 'react';
import { useSelector } from 'react-redux';
import { generateEventSourceByPageType } from '../../common/analytics/helpers/analyticsSourceHelper';
import config from '../../common/config';
import { AdUnits } from '../../common/helpers/ads/dfp';
import {
  Content,
  mapPostRawToPostInfo,
} from '../../common/helpers/data/landingPages/fetchPostsData';
import { fetchPropertyTypeLandingPageData } from '../../common/helpers/data/landingPages/fetchPropertyTypeLandingPageData';
import { fetchLandingPageTopContentData } from '../../common/helpers/data/landingPages/fetchTopContentData';
import { getLandingPageType } from '../../common/helpers/domain/landingPages';
import {
  getPropertyTypeFromUrlSegment,
  PropertyType,
} from '../../common/helpers/domain/propertyTypes';
import { extractPostIds } from '../../components/articleFeed/articleFeedHelper';
import { getPostCategoryTabDefinition } from '../../components/articleFeed/articleFeedTab';
import { Breadcrumb } from '../../components/global/breadcrumb/Breadcrumb';
import { PostInfo } from '../../components/global/PostList/PostCard';
import { PostList } from '../../components/global/PostList/PostList';
import { Highlights } from '../../components/highlights';
import HeroArticle from '../../components/homePage/heroArticle/HeroArticle';
import HomeSeoHeader from '../../components/homePage/homeSeoHeader/HomeSeoHeader';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import { generatePropertyTypeLandingPageBreadcrumbList } from '../../components/landingPage/breadcrumb';
import BillboardAd from '../../components/landingPageAds/BillboardAd';
import MainTitle from '../../components/mainTitle/MainTitle';
import MostPopularPosts from '../../components/mostPopular/MostPopularPosts';
import {
  DynamicComponentForArticleFeedModule,
  DynamicComponentForEditorsPicks,
  DynamicComponentForFeaturedVideos,
  DynamicComponentForHeroVideo,
  DynamicComponentForInPageSearch,
  DynamicComponentForLocationRecommendation,
  DynamicComponentForNewsGuidesDecors,
} from '../../components/pages/homePage/DynamicComponents';
import {
  AsideContainer,
  LeftContainer,
  TopContentWrapper,
} from '../../components/pages/homePage/HomePage.style';
import { PropertyTypeTabs } from '../../components/propertyTypeTabs/PropertyTypeTabs';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import Row from '../../components/rows/Row';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import Page from '../../layouts/main';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import commonResource from '../../locales/th/common.json';
import homeResource from '../../locales/th/home.json';
import { HeroArticleInfo } from '../../queries/homePage/heroArticles';
import { HeroVideoInfo } from '../../queries/homePage/heroVideo';
import { HighlightItem } from '../../queries/homePage/highlights';
import { ArticleBlockPost } from '../../queries/homePage/articleBlock';
import { RecommendedLocation } from '../../queries/homePage/locationRecommendation';
import { MostPopular } from '../../queries/homePage/mostPopular';
import { updatePageType } from '../../store/home/slice';
import ViewMoreLink from '../../components/ViewMoreLink/ViewMoreLink';
import { PostSubcategory } from '../../common/helpers/domain/postCategories';
import PostListModule from '../../components/landingPage/PostListModule';
import { updateCurrentSourcePage } from '../../store/search/slice';
import { WholeState } from '../../store/common/type';
import {
  trackArticle,
  trackArticleFeedShowMore,
  trackArticleFeedTab,
  trackEditorsPicksArticle,
  trackEditorsPicksCarousel,
  trackHeroArticle,
  trackHeroVideoArticle,
  trackHighLightArticle,
  trackMostPopularArticle,
  trackPropertyTypeTab,
  trackRecommendedLocationCard,
  trackRecommendedLocationCarouselArrow,
  trackViewMoreLink,
} from '../../common/analytics/analyticsTracker';
import { getNewsGuidesDecorAnalyticsFeature } from '../../common/analytics/helpers/analyticsFeatureHelper';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';
import { getAdSectByPageType } from '../../common/helpers/ads/AdsHelper';

interface HomePageProps {
  canonicalLink: string;
  propertyType: PropertyType;
  heroArticleData: HeroArticleInfo;
  popularArticlesData: MostPopular[];
  highLightTopics: HighlightItem[];
  reviewPostsData: Content[];
  previewPostsData: Content[];
  povPostsData: Content[];
  newsPostsData: Content[];
  guidesPostsData: Content[];
  decorPostsData: Content[];
  heroVideoData: HeroVideoInfo;
  featuredVideosData: Content[];
  recommendedLocations: RecommendedLocation[];
  editorsPicksData: PostInfo[];
}

function HomePage({
  canonicalLink,
  propertyType,
  heroArticleData,
  popularArticlesData,
  highLightTopics,
  reviewPostsData,
  previewPostsData,
  povPostsData,
  newsPostsData,
  guidesPostsData,
  decorPostsData,
  heroVideoData,
  featuredVideosData,
  recommendedLocations,
  editorsPicksData,
}: HomePageProps) {
  const isHomePage = !propertyType;
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );
  const landingPageType = getLandingPageType([], propertyType);
  const analyticsSource = generateEventSourceByPageType(landingPageType);

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticsSource}
    >
      <HomeSeoHeader pageType={landingPageType} canonicalLink={canonicalLink} />
      {propertyType && (
        <Breadcrumb
          items={generatePropertyTypeLandingPageBreadcrumbList(propertyType)}
        />
      )}
      <Row verticalSpacing={0} maxWidth={homePageRowSetting.maxWidth}>
        <MainTitle
          title={homeResource.title[propertyType || 'main']}
          subtitle={homeResource.subtitle[propertyType || 'main']}
        />
      </Row>
      <Row
        verticalSpacing={0}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <TopContentWrapper>
          <LeftContainer>
            <HeroArticle
              heroArticleData={heroArticleData}
              onClickHeroArticle={(heroArticle) =>
                trackHeroArticle(
                  userInfo,
                  heroArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_ARTICLE,
                )
              }
            />
          </LeftContainer>
          <AsideContainer>
            {propertyType && (
              <MostPopularPosts
                post={popularArticlesData}
                onClick={(popularArticle) =>
                  trackMostPopularArticle(
                    userInfo,
                    popularArticle,
                    analyticsSource,
                    ANALYTICS_FEATURES.POPULAR,
                  )
                }
              />
            )}
            {!propertyType && (
              <Highlights
                highlights={highLightTopics}
                onClickHighlightCard={(highLight) =>
                  trackHighLightArticle(
                    userInfo,
                    highLight,
                    analyticsSource,
                    ANALYTICS_FEATURES.HIGHLIGHTS,
                  )
                }
              />
            )}
          </AsideContainer>
        </TopContentWrapper>
      </Row>
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard1}
          sect={getAdSectByPageType(landingPageType)}
        />
      </Row>
      {isHomePage && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <ModuleTitle title={commonResource.propertyTypeTabs.title} />
          <PropertyTypeTabs
            onClickTabCard={(tabArticle) => {
              trackPropertyTypeTab(userInfo, analyticsSource, tabArticle);
            }}
          />
        </Row>
      )}
      {reviewPostsData.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="review"
        >
          <PostListModule
            title={commonResource.postModules.home.review.title}
            subtitle={commonResource.postModules.home.review.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={getLandingPageType(
                  [PostSubcategory.Review],
                  propertyType,
                )}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    generateEventSourceByPageType(targetLandingPage),
                    ANALYTICS_FEATURES.PROJECT_REVIEW,
                  );
                }}
              />
            }
          >
            <PostList
              contents={reviewPostsData}
              adUnit={AdUnits.HP_Pos1}
              adSect={getAdSectByPageType(landingPageType)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.PROJECT_REVIEW,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      {recommendedLocations.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <ModuleTitle
            title={homeResource.locationRecommendation.title}
            subtitle={homeResource.locationRecommendation.subtitle}
          />
          <DynamicComponentForLocationRecommendation
            locations={recommendedLocations}
            onClickRecommendedLocationCarouselArrow={() =>
              trackRecommendedLocationCarouselArrow(
                userInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_HIGHLIGHT,
              )
            }
            onClickRecommendedLocationCard={(locationInfo) =>
              trackRecommendedLocationCard(
                userInfo,
                locationInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_HIGHLIGHT,
              )
            }
          />
        </Row>
      )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <DynamicComponentForInPageSearch />
      </Row>
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <BillboardAd
          adUnit={AdUnits.HP_Billboard2}
          sect={getAdSectByPageType(landingPageType)}
        />
      </Row>
      {editorsPicksData.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <ModuleTitle title={homeResource.articleBlock.title} />
          <DynamicComponentForEditorsPicks
            editorsPicks={editorsPicksData}
            onClickEditorsPicksArticle={(post, index) =>
              trackEditorsPicksArticle(
                userInfo,
                post,
                analyticsSource,
                `${ANALYTICS_FEATURES.EDITORS_PICK}-${index + 1}`,
              )
            }
            onClickEditorsPicksCarousel={() => {
              trackEditorsPicksCarousel(
                userInfo,
                analyticsSource,
                ANALYTICS_FEATURES.EDITORS_PICK,
              );
            }}
          />
        </Row>
      )}
      {(heroVideoData || featuredVideosData.length > 0) && (
        <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
          <div style={{ overflowX: 'hidden' }}>
            <DynamicComponentForHeroVideo
              heroVideo={heroVideoData}
              onClick={(heroVideo) =>
                trackHeroVideoArticle(
                  userInfo,
                  heroVideo,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_VIDEO,
                )
              }
            />
            <DynamicComponentForFeaturedVideos
              contents={featuredVideosData}
              onClickVideoCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.VIDEO,
                )
              }
            />
          </div>
        </Row>
      )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <BillboardAd
          adUnit={AdUnits.HP_Billboard3}
          sect={getAdSectByPageType(landingPageType)}
        />
      </Row>
      {previewPostsData.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="preview"
        >
          <PostListModule
            title={commonResource.postModules.home.preview.title}
            subtitle={commonResource.postModules.home.preview.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={getLandingPageType(
                  [PostSubcategory.Preview],
                  propertyType,
                )}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    generateEventSourceByPageType(targetLandingPage),
                    ANALYTICS_FEATURES.PROJECT_PREVIEW,
                  );
                }}
              />
            }
          >
            <PostList
              contents={previewPostsData}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.PROJECT_PREVIEW,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <BillboardAd
          adUnit={AdUnits.HP_Billboard4}
          sect={getAdSectByPageType(landingPageType)}
        />
      </Row>
      {povPostsData.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="point-of-view"
        >
          <PostListModule
            title={commonResource.postModules.home.pov.title}
            subtitle={commonResource.postModules.home.pov.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={getLandingPageType(
                  [PostSubcategory.PointOfView],
                  propertyType,
                )}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    generateEventSourceByPageType(targetLandingPage),
                    ANALYTICS_FEATURES.POINT_OF_VIEW,
                  );
                }}
              />
            }
          >
            <PostList
              contents={povPostsData}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.POINT_OF_VIEW,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      {(guidesPostsData.length > 0 ||
        newsPostsData.length > 0 ||
        decorPostsData.length > 0) && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <DynamicComponentForNewsGuidesDecors
            guidesData={guidesPostsData}
            newsData={newsPostsData}
            decorsData={decorPostsData}
            onClickNewsArticle={(newsArticle, viewMorePageType) => {
              trackArticle(
                userInfo,
                newsArticle,
                analyticsSource,
                getNewsGuidesDecorAnalyticsFeature({
                  landingPageType: viewMorePageType,
                }),
              );
            }}
            onClickNewsViewMoreLink={(targetLandingPage) => {
              trackViewMoreLink(
                userInfo,
                analyticsSource,
                generateEventSourceByPageType(targetLandingPage),
                getNewsGuidesDecorAnalyticsFeature({
                  landingPageType: targetLandingPage,
                }),
              );
            }}
          />
        </Row>
      )}
      {isHomePage && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <BillboardAd
            adUnit={AdUnits.HP_Billboard5}
            sect={getAdSectByPageType(landingPageType)}
          />
        </Row>
      )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <ModuleTitle title={homeResource.readMore.title} />
        <DynamicComponentForArticleFeedModule
          tabDefinition={getPostCategoryTabDefinition(propertyType)}
          adUnit={AdUnits.HP_MrecInfiniteAd}
          adSect={getAdSectByPageType(landingPageType)}
          excludePostIds={extractPostIds(
            [
              ...reviewPostsData,
              ...previewPostsData,
              ...povPostsData,
              ...newsPostsData,
              ...guidesPostsData,
              ...decorPostsData,
              ...featuredVideosData,
            ],
            heroVideoData,
          )}
          onClickPostCard={(post) =>
            trackArticle(
              userInfo,
              post,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            )
          }
          onClickArticleFeedShowMore={() => {
            trackArticleFeedShowMore(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            );
          }}
          onClickArticleFeedTab={(selectedTab) => {
            trackArticleFeedTab(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
              selectedTab,
            );
          }}
        />
      </Row>
      <Row verticalSpacing={16} fullWidth>
        <SeoFooter />
      </Row>
    </Page>
  );
}

HomePage.getInitialProps = async ({
  store,
  query,
  asPath,
  apolloClient,
  res,
}: CustomNextPageContext): Promise<HomePageProps> => {
  const propertyTypeInTh = query.propertyType as string;
  const propertyType = getPropertyTypeFromUrlSegment(propertyTypeInTh);
  const canonicalLink = config.baseUrl + asPath.substring(1);

  // When there is no propertyType, then redirect to root
  if (isEmpty(query) && isEmpty(propertyTypeInTh) && !res) {
    // when the code is executing on client-side
    Router.push('/');
  }

  try {
    const { heroArticle, mostPopular, highlights, recommendedLocations } =
      await fetchLandingPageTopContentData(apolloClient, { propertyType });

    const excludePostIds = heroArticle?.id ? [heroArticle?.id] : [];
    const { review, preview, pov, news, guides, decor, video, editorsPicks } =
      await fetchPropertyTypeLandingPageData(
        apolloClient,
        excludePostIds,
        propertyType,
      );

    const editorsPicksMappedData = editorsPicks.map((data: ArticleBlockPost) =>
      mapPostRawToPostInfo(data),
    );

    const landingPageType = getLandingPageType([], propertyType);
    store.dispatch(updatePageType(landingPageType));
    store.dispatch(
      updateCurrentSourcePage(generateEventSourceByPageType(landingPageType)),
    );
    return {
      propertyType,
      canonicalLink,
      heroArticleData: heroArticle,
      popularArticlesData: mostPopular,
      highLightTopics: highlights,
      reviewPostsData: review,
      previewPostsData: preview,
      povPostsData: pov,
      newsPostsData: news,
      guidesPostsData: guides,
      decorPostsData: decor,
      heroVideoData: video.heroVideo,
      featuredVideosData: video.featuredVideos,
      recommendedLocations,
      editorsPicksData: editorsPicksMappedData,
    };
  } catch (e) {
    console.log('Error when fetch homepage article and posts:', e);
  }
};

export default HomePage;
